import { mensagemDeErro } from '@/lib/erros';
import { ApiError } from '@/lib/http';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Dialog } from '@/ui/primitives/dialog';
import { type ClassificacaoPersonalizada } from '@fiscoai/contracts';

import { useApagarClassificacao } from './api';
import { uso } from './textos';

interface Props {
  empresaId: string;
  classificacao: ClassificacaoPersonalizada | null;
  aoFechar: () => void;
  /** Em uso, o servidor recusa apagar: o diálogo oferece desativar no lugar. */
  aoDesativar: (c: ClassificacaoPersonalizada) => void;
}

/** "Em uso" (422) fica no diálogo, com a saída: desativar. Outros erros viram aviso. */
function recusa(erro: unknown): string | null {
  return erro instanceof ApiError && erro.status === 422 ? mensagemDeErro(erro) : null;
}

/** Confirma apagar; se a classificação estiver em uso, mostra o porquê e oferece desativar. */
export function DialogoApagarClassificacao({ empresaId, classificacao: c, ...p }: Props) {
  const apagar = useApagarClassificacao(empresaId);
  const emUso = recusa(apagar.error);
  const fechar = () => {
    apagar.reset();
    p.aoFechar();
  };
  const confirmar = async () => {
    if (!c) return;
    try {
      await apagar.mutateAsync(c.id);
      toast.success('Classificação apagada');
      fechar();
    } catch (erro) {
      if (!recusa(erro)) toast.error(mensagemDeErro(erro));
    }
  };
  return (
    <Dialog
      aberto={c !== null}
      aoMudar={(aberto) => {
        if (!aberto) fechar();
      }}
      titulo={c ? `Apagar a classificação ${c.nome}?` : 'Apagar classificação'}
      descricao={c ? `${uso(c)}.` : undefined}
      acoes={
        <>
          <Button variante="fantasma" onClick={fechar}>
            Cancelar
          </Button>
          {emUso && c?.ativa ? (
            <Button
              variante="primario"
              onClick={() => {
                p.aoDesativar(c);
                fechar();
              }}
            >
              Desativar classificação
            </Button>
          ) : (
            <Button
              variante="perigo"
              carregando={apagar.isPending}
              disabled={emUso !== null}
              onClick={() => void confirmar()}
            >
              Apagar classificação
            </Button>
          )}
        </>
      }
    >
      {emUso ? (
        <p role="alert" className="rounded-controle bg-alerta-suave px-3 py-2 text-sm text-alerta">
          {emUso}
        </p>
      ) : (
        <p className="text-sm text-texto-suave">
          Só dá para apagar a que nenhum item nem regra usa. As outras se desativam.
        </p>
      )}
    </Dialog>
  );
}
